import React, { useCallback } from 'react';
import { componentWillAppendToBody } from 'react-append-to-body';
import styled, { useTheme } from 'styled-components';

import {
  animations,
  colors,
  maxWidthBreakpoints,
  media,
  Theme,
  useKeypress,
  useMediaQuery,
} from '../../../core';
import Button from '../Button';
import Card from '../Card';
import Heading from '../Heading';
import Icon from '../Icon';
import LinkButton from '../LinkButton/LinkButton';

export interface ModalProps {
  children?: React.ReactNode;
  onClose?: () => void;
  title: string;
  width?: string;
  showButtons?: boolean;
  onPress?: () => void;
  submitText?: string;
  cancelText?: string;
  sectionMargin?: string;
  backdropMode?: 'dark' | 'light';
  variant: 'primary' | 'secondary';
  enableBackgroundClose?: boolean;
  footer?: React.ReactNode;
  type?: 'form' | 'default';
}

export interface ModalFooterProps {
  children: React.ReactNode;
}

export interface ModalScrollableContentProps {
  isFooter: boolean;
  children: React.ReactNode;
}

const Background = styled.div<{
  backdropMode?: 'dark' | 'light';
  variant: 'primary' | 'secondary';
}>`
  align-items: center;
  background-color: ${({ backdropMode, variant }) =>
    backdropMode
      ? backdropMode === 'light'
        ? 'rgba(255, 255, 255, 0.8)'
        : 'rgba(0, 0, 0, 0.1)'
      : variant === 'primary'
      ? 'rgba(255, 255, 255, 0.8)'
      : 'rgba(0, 0, 0, 0.1)'};
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  width: 100%;
  z-index: 1001;

  ${({ variant }) =>
    variant === 'primary' &&
    `
      padding: 16px;
    `}
`;

const Scroll = styled.div`
  overflow-y: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const ModalCard = styled.div<{
  width?: string;
}>`
  animation: ${animations.fadeAndSlideInFromTop} 0.2s ease-out forwards;
  width: ${({ width }) => (width ? width : '760px')};
  max-height: calc(100vh);
  padding: 40px 0;

  ${media.tabletPortrait} {
    width: 90%;
  }
`;

const ModalContent = styled.div<{ width?: string }>`
  animation: ${animations.fadeAndSlideInFromTop} 0.2s ease-out forwards;
  width: ${({ width }) => (width ? width : '760px')};
  border-radius: ${({ theme }) => theme.borderRadius || '3px'};
  position: absolute;
  background-color: ${colors.white};
  overflow: hidden;
  display: flex;
  margin: 5% auto;
  flex-direction: column;
  justify-content: center;
  max-height: calc(100vh * 0.9);

  ${media.tabletPortrait} {
    width: 90%;
  }

  form {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;

    .scrollable {
      max-height: calc(100vh * 0.9 - 150px);
    }
  }
`;

const ScrollableContent = styled.div<{ isFooter?: boolean }>`
  overflow-y: auto;
  flex: 1;
  padding: ${({ isFooter }) => (isFooter ? '24px' : '24px 24px 0')};
`;

const ButtonsWrapper = styled.div<{ variant: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  justify-content: ${({ variant }) => (variant === 'primary' ? 'center' : 'flex-end')};
  padding: ${({ variant }) => (variant === 'secondary' ? '16px 24px' : '12px 0 24px')};
  gap: 16px;

  button.modalButton {
    text-decoration: none;
    padding: 0 24px;
  }

  ${media.tabletPortrait} {
    justify-content: center;
    gap: 16px;

    button {
      font-size: 14px;
      height: 48px;
    }
  }
`;

const Footer = styled(ButtonsWrapper)`
  border-top: 1px solid ${colors.neutral400};
`;

const StyledButton = styled(Button)`
  ${media.tabletPortrait} {
    margin: 0 4px;
    min-width: 40%;
  }
`;

const Header = styled.div<{ showTitle?: boolean; variant: 'primary' | 'secondary' }>`
  border-bottom: ${({ showTitle, theme }) =>
    showTitle && `1px solid ${theme.modalLineColor || colors.primary}`};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 24px;
  position: relative;

  ${({ variant, theme }) =>
    variant === 'primary' &&
    theme.name !== Theme.theme1 &&
    `
      align-items: center;
      padding: 0 0 16px;
  `}

  ${({ variant }) =>
    variant === 'secondary' &&
    `
      padding: 24px 0 16px;
      margin: 0 24px;
      align-items: center;
  `}
`;

const StyledHeading = styled(Heading).attrs({
  size: 4,
})<{ variant: 'primary' | 'secondary' }>`
  line-height: 26px;
  margin-bottom: ${({ variant }) => (variant === 'primary' ? '4px' : 0)};
  ${({ theme }) =>
    theme.name === Theme.theme1 &&
    `
    font-size: 18px;

    ${media.tabletPortrait} {
      font-size: 16px;
    }
  `}

  ${({ theme }) =>
    theme.name === Theme.theme3 &&
    `
    font-weight: 800;
    line-height: 26.4px;
  `}
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledIcon = styled(Icon)`
  cursor: pointer;
  margin-left: 20px;
`;

const Container = styled.section<{ sectionMargin?: string }>`
  margin: ${({ sectionMargin }) => (sectionMargin ? sectionMargin : '0 0 30px')};
  width: 100%;
`;

const ModalFooter = ({ children }: ModalFooterProps) => {
  return <Footer variant="secondary">{children}</Footer>;
};

const ModalScrollableContent = ({ isFooter, children }: ModalScrollableContentProps) => {
  return (
    <ScrollableContent isFooter={isFooter} className="scrollable">
      {children}
    </ScrollableContent>
  );
};

const Modal = ({
  children,
  onClose,
  showButtons,
  onPress,
  submitText,
  width,
  title,
  cancelText,
  backdropMode,
  variant = 'primary',
  enableBackgroundClose = true,
  footer,
  type = 'default',
}: ModalProps) => {
  const showCloseButton = !!onClose;
  const showTitle = !!title;
  const isXSmallMedia = useMediaQuery(maxWidthBreakpoints.xsmall);

  const theme = useTheme();

  useKeypress('Escape', () => {
    if (onClose) {
      onClose();
    }
  });

  const handleCloseClick = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleBackgroundClick = useCallback(
    (clickEvent) => {
      if (enableBackgroundClose) {
        if (clickEvent.currentTarget !== clickEvent.target) {
          return;
        }
        if (onClose) {
          onClose();
        }
      }
    },
    [onClose]
  );

  return (
    <Background onClick={handleBackgroundClick} backdropMode={backdropMode} variant={variant}>
      {variant === 'primary' && (
        <Scroll onClick={handleBackgroundClick}>
          <ModalCard width={width}>
            <Card>
              <Container>
                <Header showTitle={showTitle} variant="primary">
                  <StyledHeading>{showTitle && title}</StyledHeading>
                  <Actions>
                    {showCloseButton && (
                      <StyledIcon
                        color={theme.iconColor || colors.primary}
                        type={theme.name === Theme.theme3 ? 'close' : 'error'}
                        onClick={handleCloseClick}
                      />
                    )}
                  </Actions>
                </Header>
                {children}
              </Container>
              {showButtons && (
                <ButtonsWrapper variant="primary">
                  {onClose && (
                    <StyledButton
                      variant="secondary"
                      size={isXSmallMedia ? 'xsmall' : 'medium'}
                      onClick={onClose}
                    >
                      {cancelText ? cancelText : 'Cancel'}
                    </StyledButton>
                  )}
                  {onPress && (
                    <StyledButton
                      variant="primary"
                      size={isXSmallMedia ? 'xsmall' : 'medium'}
                      onClick={onPress}
                    >
                      {submitText ? submitText : 'OK'}
                    </StyledButton>
                  )}
                </ButtonsWrapper>
              )}
            </Card>
          </ModalCard>
        </Scroll>
      )}
      {variant === 'secondary' && (
        <ModalContent width={width}>
          <Header showTitle={showTitle} variant={variant}>
            <StyledHeading variant="secondary">{showTitle && title}</StyledHeading>
            <Actions>
              {showCloseButton && (
                <StyledIcon
                  color={theme.modalIconColor || colors.primary}
                  type={theme.name === Theme.theme3 ? 'close' : 'error'}
                  onClick={handleCloseClick}
                />
              )}
            </Actions>
          </Header>
          {type === 'form' ? (
            <React.Fragment>{children}</React.Fragment>
          ) : (
            <React.Fragment>
              <ModalScrollableContent isFooter={!!footer}>{children}</ModalScrollableContent>
              {showButtons && (
                <ButtonsWrapper variant="secondary">
                  {onClose && (
                    <LinkButton
                      size={'medium'}
                      minWidth={'auto'}
                      onClick={onClose}
                      className="modalButton"
                    >
                      {cancelText ? cancelText : 'Cancel'}
                    </LinkButton>
                  )}
                  {onPress && (
                    <LinkButton
                      size={'medium'}
                      minWidth={'auto'}
                      onClick={onPress}
                      className="modalButton"
                    >
                      {submitText ? submitText : 'Confirm'}
                    </LinkButton>
                  )}
                </ButtonsWrapper>
              )}
              {footer && <ModalFooter>{footer}</ModalFooter>}
            </React.Fragment>
          )}
        </ModalContent>
      )}
    </Background>
  );
};

const MainModal = componentWillAppendToBody(Modal);

MainModal.Footer = ModalFooter;
MainModal.ScrollableContent = ModalScrollableContent;

export default MainModal;
